import {
  Box,
  Card,
  CardMedia,
  Typography,
  IconButton,
  Chip as MChip,
} from '@mui/material';
import {
  editIcon,
  rollIcon,
  filmIcon,
  moveIcon,
  deleteIcon,
  viewEditIcon,
  saveIcon,
} from 'components/Icon/svg';
import { formatDate } from 'utils/tableListFormat';
import { targetAudiencesLabel } from 'constants/fields';
import {
  itemType,
  visibilities,
  visibilitiesColor,
  statusColor,
} from 'constants/homeApp';
import defaultImage from './defaultImage.svg';
import videoStatus from 'utils/videoStatusUtils';

export const Root = ({ children, divider, disabled }) => {
  return (
    <Card
      elevation={0}
      data-no-dnd={disabled ? 'true' : 'false'}
      disabled={disabled}
      sx={{
        padding: '19px',
        maxHeight: '164px',
        borderBottom: `solid 1px ${divider ? '#EAECF0' : '#FFF'} `,
        borderRadius: 0,
      }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'min-content auto auto',
        }}>
        {children}
      </Box>
    </Card>
  );
};

export const Description = ({
  title,
  subtitle,
  status,
  visibility,
  targetAudiences,
  children,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '24px',
      justifyContent: 'center',
    }}>
    <Typography
      variant="subtitle1"
      fontSize="14px"
      lineHeight="20px"
      fontWeight="bold"
      color="#6F6B7D">
      {title}
    </Typography>
    <Typography
      variant="body2"
      lineHeight="20px"
      fontSize="14px"
      color="#6F6B7D">
      {subtitle}
    </Typography>
    <Typography
      variant="body2"
      lineHeight="20px"
      fontSize="12px"
      color="#6F6B7D">
      {targetAudiences && !!targetAudiences?.length && (
        <>
          Público-alvo:{' '}
          {targetAudiences.length === 5
            ? 'Todos'
            : targetAudiences
                ?.map(i => targetAudiencesLabel[i.code])
                .join(', ')}
          

        </>
      )}
    </Typography>
    {status && (
      <Typography
        variant="body2"
        lineHeight="20px"
        fontSize="12px"
        color="#6F6B7D">
        Status:{' '}
        <Box as="span" sx={{ color: statusColor?.[status] || 'inherit' }}>
          {videoStatus?.[status] ?? status}
        </Box>
      </Typography>
    )}
    {visibility && (
      <Typography
        variant="body2"
        lineHeight="20px"
        fontSize="12px"
        color="#6F6B7D">
        {visibility && (
          <>
            Visibilidade:{' '}
            <Box
              as="span"
              sx={{
                color: visibilitiesColor?.[visibility] || 'inherit',
              }}>
              {visibilities?.[visibility]}
            </Box>
          </>
        )}
      </Typography>
    )}
    {children}
  </Box>
);
 
export const DescriptionDetail = ({
  type,
  title,
  status,
  expiresIn,
  contentType,
  visibility = 'INTERNAL_USERS',
  targetAudiences,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '24px',
      justifyContent: 'center',
    }}>
    <Typography
      variant="subtitle1"
      fontSize="12px"
      lineHeight="20px"
      fontWeight="bold"
      color="#6F6B7D">
      {itemType?.[type || contentType]}: {title}
    </Typography>

    <Typography
      variant="body2"
      lineHeight="20px"
      fontSize="12px"
      color="#6F6B7D">
      {targetAudiences && !!targetAudiences?.length && (
        <>
          Público-alvo:{' '}
          {targetAudiences.length === 5
            ? 'Todos'
            : targetAudiences
                ?.map(i => targetAudiencesLabel[i.code])
                .join(', ')}
          <br />
        </>
      )}
      {visibility && (
        <>
          Visibilidade:{' '}
          <Box
            as="span"
            sx={{
              color: visibilitiesColor?.[visibility] || 'inherit',
            }}>
            {visibilities?.[visibility]}
          </Box>
        </>
      )}
      {status && (
        <>
          <br />
          Status:{' '}
          <Box
            as="span"
            sx={{
              color: expiresIn ? new Date() > new Date(expiresIn) ? statusColor.EXPIRED : statusColor?.[status] || 'inherit' : statusColor?.[status] || 'inherit',
            }}>
            { 
              expiresIn ? new Date() > new Date(expiresIn) ? videoStatus.EXPIRED : videoStatus?.[status] : videoStatus?.[status]
            }
          </Box>
        </>
      )}
      {[type, contentType].includes('VIDEO') && (
        <>
          <br />
          {formatDate(expiresIn)}
        </>
      )}
    </Typography>
  </Box>
);

export const Image = ({
  image = defaultImage,
  title,
  children,
  variant = 'square',
}) => {
  const sizes = {
    vertical: {
      width: '88px',
      height: '124px',
    },
    square: {
      width: '124px',
      height: '124px',
    },
  };

  return (
    <CardMedia
      sx={{
        ...sizes[variant?.toLowerCase() || 'square'],
        position: 'relative',
        border: 'solid 1px #EAEAEA',
        alignSelf: 'center',
      }}
      image={image || defaultImage}
      title={title}>
      {children}
    </CardMedia>
  );
};

export const CornerAdornment = ({ name }) => {
  const names = {
    roll: {
      icon: rollIcon,
      alt: 'ícone Coleção',
    },
    film: {
      icon: filmIcon,
      alt: 'ícone Vídeo',
    },
  };

  return (
    <Box
      sx={{
        width: '27px',
        height: '27px',
        position: 'absolute',
        right: '-13px',
        bottom: '-13px',
        backgroundImage: `url(${names[name]?.icon})`,
      }}
    />
  );
};

export const ActionsContainer = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
    <Box
      sx={{ display: 'flex' }}
      style={{
        maxHeight: '40px',
      }}>
      {children}
    </Box>
  </Box>
);

export const ActionButton = ({ action = 'delete', ...props }) => {
  const actions = {
    delete: {
      icon: deleteIcon,
      aria: 'Apagar',
    },
    move: {
      icon: moveIcon,
      aria: 'Mover',
    },
    viewEdit: {
      icon: viewEditIcon,
      aria: 'Ver ou editar',
    },
    edit: {
      icon: editIcon,
      aria: 'Editar',
    },
    save: {
      icon: saveIcon,
      aria: 'Salvar',
    },
  };

  return (
    <IconButton
      aria-label={actions[action].aria}
      sx={{
        width: '40px',
        height: '40px',
        backgroundImage: `url(${actions[action].icon})`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
      {...props}
    />
  );
};

export const Chip = ({ label }) => (
  <MChip
    size="small"
    label={label}
    variant="outlined"
    sx={{
      width: 'fit-content',
      borderColor: '#E4E4E4',
    }}
  />
);
